import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "./AuthContext";

const WorkspaceContext = createContext(null);
const STORAGE_KEY = "lg_current_workspace_id";

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [currentId, setCurrentId] = useState(
    typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) || "" : ""
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user || user === false) {
      setWorkspaces([]);
      setLoading(false);
      return [];
    }
    setLoading(true);
    try {
      const res = await api.get("/workspaces");
      const list = res.data || [];
      setWorkspaces(list);
      // If no current id, or it points to a workspace we no longer have access to,
      // fall back to the personal workspace.
      const exists = list.find((w) => w.id === currentId);
      if (!exists) {
        const personal = list.find((w) => w.is_personal) || list[0];
        if (personal) {
          setCurrentId(personal.id);
          localStorage.setItem(STORAGE_KEY, personal.id);
        }
      }
      return list;
    } finally {
      setLoading(false);
    }
  }, [user, currentId]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const switchTo = useCallback((workspaceId) => {
    setCurrentId(workspaceId);
    localStorage.setItem(STORAGE_KEY, workspaceId);
  }, []);

  const current = workspaces.find((w) => w.id === currentId) || null;

  return (
    <WorkspaceContext.Provider
      value={{ workspaces, current, currentId, switchTo, refresh, loading }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within a <WorkspaceProvider>");
  return ctx;
}

export function getActiveWorkspaceId() {
  if (typeof window === "undefined") return "";
  return localStorage.getItem(STORAGE_KEY) || "";
}
